import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import {
  capitalizeFirstLetter,
  truncateText,
} from "../../../../../utils/string-helpers";
import { calculateMacros } from "../../../../../utils/nutrition-util";

const TYPE_LABEL_KEYS = {
  food: "recentFoods.locationFoods",
  meal: "recentFoods.locationMeals",
  recipe: "recentFoods.locationRecipes",
};

export const FoodSearchResults = ({
  data,
  isLoading,
  error,
  onItemSelect,
  hasMore,
  isLoadingMore,
  onLoadMore,
}) => {
  const { t } = useTranslation("athlete-portal");
  const [showNoResults, setShowNoResults] = useState(false);

  /**
   * UseEffect to add a litte delay to show the not results message
   */
  useEffect(() => {
    let timeout;
    if (!isLoading && !error && data.length === 0) {
      timeout = setTimeout(() => setShowNoResults(true), 1500);
    } else {
      setShowNoResults(false);
      clearTimeout(timeout);
    }

    return () => clearTimeout(timeout);
  }, [data, isLoading, error]);

  if (showNoResults) {
    return (
      <div className="results">
        <div className="status-message">
          <div className="no-results">
            <span className="opacity-70">
              {t("foodSearchResults.noResultsFound")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="results">
      {isLoading && (
        <div className="status-message">
          <div className="spinner-div">
            <img src="/img/spinner.gif" width="50" height="50" alt="spinner" />
          </div>
        </div>
      )}

      {!isLoading && error && data.length === 0 && (
        <div className="status-message">
          <div className="no-results">
            <span className="opacity-70">
              {t("foodSearchResults.somethingWentWrong")}
            </span>
          </div>
        </div>
      )}

      {data.map((item, index) => {
        const {
          _id,
          quantityNumber = 1,
          quantityDescription = "Serving",
          name,
          type,
          foods = [],
          proteins: itemProteins,
          carbs: itemCarbs,
          fats: itemFats,
          calories: itemCalories,
          brand,
          source,
        } = item;
        const isMeal = type === "meal";
        const isRecipe = type === "recipe";
        const icon = `/img/new-icons/${type === "verified-food" ? "food" : type}.svg`;

        const quantityDescriptionToShow =
          type === "recipe" && quantityNumber > 1
            ? quantityDescription.toLowerCase() === "batch"
              ? "batches"
              : `${quantityDescription}s`
            : type === "food" && quantityDescription === "Servings"
              ? quantityNumber === 1
                ? "Serving"
                : "Servings"
              : quantityDescription;

        const servings = `${quantityNumber} ${quantityDescriptionToShow}`;

        const itemDetails = {
          itemProteins,
          itemCarbs,
          itemFats,
          itemCalories,
          foodKey: "foodId",
        };

        const { proteins, carbs, fats, calories } = calculateMacros({
          isMeal,
          isRecipe,
          foods,
          itemDetails,
        });

        const uniqueKey = `${source || type || "item"}-${_id}-${index}`;
        return (
          <button
            className="content"
            key={uniqueKey}
            onClick={() => onItemSelect(item)}
          >
            <div className="info-food">
              <div>
                {brand && !name.toLowerCase().includes(brand.toLowerCase())
                  ? `${name} (${brand})`
                  : name}
              </div>
              <div className="nutrition-info">
                <span className="servings-span">
                  {truncateText(servings, 18)}
                </span>
                <span>
                  {t("foodItems.protein")} {proteins}g{" "}
                </span>
                <span>&#8226;</span>
                <span>
                  {t("foodItems.carbs")} {carbs}g
                </span>
                <span>&#8226;</span>
                <span>
                  {t("foodItems.fat")} {fats}g
                </span>
                <span>&#8226;</span>
                <span>
                  {t("foodItems.calories")} {Math.round(calories)}
                </span>
              </div>
            </div>
            <div className="identifier" style={{ width: "4rem" }}>
              <SvgIcon src={icon} noFilterOnLight={false} />
              <span>
                {source ||
                  (TYPE_LABEL_KEYS[type]
                    ? t(TYPE_LABEL_KEYS[type])
                    : capitalizeFirstLetter(type))}
              </span>
            </div>
          </button>
        );
      })}
      {!isLoading &&
        hasMore &&
        onLoadMore &&
        (isLoadingMore ? (
          <div className="load-more-status">
            <img
              src="/img/spinner.gif"
              width={24}
              height={24}
              alt=""
              aria-hidden
            />
            <span>Loading…</span>
          </div>
        ) : (
          <div className="load-more-container">
            <button
              type="button"
              data-button-type="outline"
              onClick={onLoadMore}
              disabled={isLoadingMore}
            >
              Load more
            </button>
          </div>
        ))}
    </div>
  );
};

FoodSearchResults.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onItemSelect: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
};
